import React, { useEffect, useRef, useState } from "react";

const SurfingLogo = () => {
  const [direction, setDirection] = useState(0); // -1 = up, +1 = down
  const [isMobile, setIsMobile] = useState(false);
  const targetRef = useRef(0); // target progress 0..1
  const currentRef = useRef(0); // smoothed progress
  const elRef = useRef(null);
  const rafRef = useRef(null);
  const lastYRef = useRef(0);

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener("resize", checkMobile);
    lastYRef.current = window.scrollY;

    const onScroll = () => {
      const y = window.scrollY;
      const max = Math.max(document.body.scrollHeight - window.innerHeight, 1);
      targetRef.current = Math.max(0, Math.min(1, y / max));
      const dy = y - lastYRef.current;
      if (Math.abs(dy) > 1) setDirection(dy > 0 ? 1 : -1);
      lastYRef.current = y;
    };

    // Smoothing loop — lerp current toward target every frame for buttery motion
    const animate = () => {
      const diff = targetRef.current - currentRef.current;
      currentRef.current += diff * 0.08; // smaller = smoother but slower to catch up
      if (elRef.current) {
        const p = currentRef.current;
        const t = p < 0.5 ? p * 2 : (1 - p) * 2;
        const range = window.innerWidth < 768
          ? { min: 8, max: 62 }
          : { min: 4, max: 92 };
        const leftPct = range.min + t * (range.max - range.min);
        elRef.current.style.left = `${leftPct}%`;
      }
      rafRef.current = requestAnimationFrame(animate);
    };

    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    rafRef.current = requestAnimationFrame(animate);

    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", checkMobile);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, []);

  const tilt = direction * 8;

  // Icon-only display (Buddha + fish + wave, without the wordmark)
  // The icon takes the top ~80% of the logo image (Buddha sits on the fish on a wave)
  const iconSize = isMobile ? 78 : 120; // width in px
  const iconHeight = Math.round(iconSize * 0.78);

  return (
    <div
      className="pointer-events-none fixed bottom-0 left-0 right-0 z-[55]"
      aria-hidden="true"
    >
      {/* Wavy line beneath the surfer */}
      <svg
        viewBox="0 0 1440 80"
        preserveAspectRatio="none"
        className="absolute bottom-0 left-0 w-full h-16 md:h-20 opacity-45"
      >
        <defs>
          <linearGradient id="wave-grad" x1="0" x2="1" y1="0" y2="0">
            <stop offset="0%" stopColor="#0b3a5f" stopOpacity="0" />
            <stop offset="50%" stopColor="#0b3a5f" stopOpacity="0.5" />
            <stop offset="100%" stopColor="#0b3a5f" stopOpacity="0" />
          </linearGradient>
        </defs>
        <path
          d="M0,40 C180,10 360,70 540,40 C720,10 900,70 1080,40 C1260,10 1440,70 1440,40 L1440,80 L0,80 Z"
          fill="url(#wave-grad)"
        >
          <animate
            attributeName="d"
            dur="6s"
            repeatCount="indefinite"
            values="
              M0,40 C180,10 360,70 540,40 C720,10 900,70 1080,40 C1260,10 1440,70 1440,40 L1440,80 L0,80 Z;
              M0,40 C180,70 360,10 540,40 C720,70 900,10 1080,40 C1260,70 1440,10 1440,40 L1440,80 L0,80 Z;
              M0,40 C180,10 360,70 540,40 C720,10 900,70 1080,40 C1260,10 1440,70 1440,40 L1440,80 L0,80 Z
            "
          />
        </path>
      </svg>

      {/* The surfing logo */}
      <div
        ref={elRef}
        className="absolute -translate-x-1/2 will-change-transform"
        style={{
          left: "4%",
          bottom: isMobile ? "18px" : "22px",
          transform: `rotate(${tilt}deg)`,
          transition: "transform 350ms ease-out",
        }}
      >
        <div
          style={{ animation: "surf-bob 2.4s ease-in-out infinite" }}
          className="drop-shadow-xl"
        >
          {/* Icon-only crop: shows Buddha + fish + wave, hides bottom wordmark */}
          <div
            style={{
              width: `${iconSize}px`,
              height: `${iconHeight}px`,
              backgroundImage: "url(/logo.png)",
              backgroundRepeat: "no-repeat",
              backgroundPosition: "top center",
              backgroundSize: `${iconSize}px auto`,
            }}
          />
        </div>
      </div>
    </div>
  );
};

export default SurfingLogo;
